import { logger } from '@adornis/base/logging.js';
import type { Dictionary, Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { Arg, Entity, Field, Query, Subscription } from '@adornis/baseql/decorators.js';
import type { EntityData, PartialEntityData } from '@adornis/baseql/entities/types.js';
import { getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Subject } from '@chancenwerk/enums/enums.js';
import { catchError, from, of, switchMap } from 'rxjs';
import { CourseFilter } from '../filter/CourseFilter.js';
import { StudentFilter } from '../filter/StudentFilter.js';
import { KPF } from '../master-data-management/kpf/entities/KPF.js';
import { ExternerMitarbeiter } from '../master-data-management/users/entities/ExternerMitarbeiter.js';
import { Student } from '../master-data-management/users/entities/Student.js';
import { Course } from './Course.js';

@Entity()
export class CourseDigitalFilter extends CourseFilter {
  static override _class = 'CourseDigitalFilter';

  constructor(doc: Partial<EntityData<CourseDigitalFilter>>) {
    super(doc);
  }

  @Field(() => [String])
  subjects?: string[];

  @Field(() => Int)
  jahrgangsstufe?: Int;

  private static _getSubjectFilter(subject: string) {
    if (subject === Subject.BIOLOGIE || subject === Subject.CHEMIE || subject === Subject.PHYSIK)
      return Subject.NATURWISSENSCHAFTEN;
    return subject;
  }

  private static _buildSubjects(subjects: string[]) {
    const filter = [] as any[];
    for (const subject of subjects) {
      if (subject === '') continue;
      if (subject === Subject.DEUTSCH) {
        filter.push(this._getSubjectFilter(Subject.DAZ));
      }
      filter.push(this._getSubjectFilter(subject));
    }
    return filter;
  }

  override getMongoFilter(): Dictionary<any> {
    const obj = this.toObject();
    const dic = super.getMongoFilter();

    dic._class = CourseDigital._class;

    if (obj.jahrgangsstufe) {
      dic.jahrgangsstufe = obj.jahrgangsstufe;
    }

    if (obj.subjects && obj.subjects.length > 0) {
      dic.subject = { $in: CourseDigitalFilter._buildSubjects(obj.subjects) };
    }

    return dic;
  }
}

/**
 * @entity CourseDigital
 */
@Entity()
export class CourseDigital extends Course {
  public static override _class = 'CourseDigital';

  constructor(doc: PartialEntityData<CourseDigital>) {
    super(doc);
  }
  @Field(() => [String], { default: v => v ?? [] })
  public linkedKPFIDs: Maybe<string[]>;

  @DataLoadedField(() => [KPF], ({ linkedKPFIDs }) => linkedKPFIDs ?? [])
  public linkedKPFs: Maybe<KPF[]>;

  @Field(() => String)
  public subject: Maybe<Subject>;

  @Field(() => Int)
  public jahrgangsstufe?: number;

  @Field(() => Boolean, { default: v => v ?? false })
  public changeNotification?: boolean;

  @DataLoadedField(() => [ExternerMitarbeiter], ({ diKuIDs }) => diKuIDs ?? [])
  public diKus: Maybe<ExternerMitarbeiter[]>;

  @Field(() => [String], { default: v => v ?? [] })
  public diKuIDs: Maybe<string[]>;

  @Subscription(() => [CourseDigital])
  public static subscribeDigitialCourseByKPFID(@Arg('kpfID', () => String) kpfID: string) {
    return (gqlFields: BaseQLSelectionSet<CourseDigital>) => {
      const selector = { _class: 'CourseDigital', linkedKPFIDs: kpfID };
      const handle = getCollectionHandle<CourseDigital>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector);
        }),
        catchError(e => {
          logger.error(e);
          return of([] as CourseDigital[]);
        }),
      );
    };
  }

  @Query(() => Int)
  public static getDigitalCourseOpenStudents(@Arg('courseID', () => String) courseID: string) {
    return async () => {
      const course = await this.getByID<CourseDigital>(courseID)({ subject: 1, jahrgangsstufe: 1, osStudentIDs: 1 });

      if (!course) return 0;

      const filter = new StudentFilter({
        active: true,
        studentType: true,
        jahrgang: course.jahrgangsstufe,
        preferedSubject: course.subject ? [course.subject] : undefined,
      });

      const col = await getRawCollection(Student._collectionName);
      const res = await col
        .aggregate([
          { $match: { ...filter.getMongoFilter(), _id: { $nin: course.osStudentIDs ?? [] } } },
          { $project: { _id: 1 } },
          { $count: 'count' },
        ])
        .toArray();

      return res[0]?.count ?? 0;
    };
  }

  private static _getSubjectFilter(subject: string) {
    if (subject === Subject.BIOLOGIE || subject === Subject.CHEMIE || subject === Subject.PHYSIK)
      return { subject: Subject.NATURWISSENSCHAFTEN };
    return { subject };
  }

  private static _buildOrFilter(subjects: string[]) {
    const filter = [] as any[];
    for (const subject of subjects) {
      if (subject === '') continue;
      if (subject === Subject.DEUTSCH) {
        filter.push(this._getSubjectFilter(Subject.DAZ));
      }
      filter.push(this._getSubjectFilter(subject));
    }
    return filter;
  }

  @Subscription(() => [CourseDigital])
  public static subscribeDigitialCourseSubjectAndGrade(
    @Arg('subjects', () => [String]) subjects: string[],
    @Arg('jahrgangsstufe', () => Int) jahrgangsstufe: number,
  ) {
    return (gqlFields: BaseQLSelectionSet<CourseDigital>) => {
      const selector = {
        _class: 'CourseDigital',
        $and: [
          { $or: [{ jahrgangsstufe: { $lte: jahrgangsstufe } }, { jahrgangsstufe: null }] },
          { $or: this._buildOrFilter(subjects) },
        ],
      };
      if (!selector.$and.at(1)?.$or.length) {
        // * When no subjects is selected, show all:
        // selector.$and.splice(1, 1);

        // * Don't show any if none are selected
        selector.$and.at(1)!.$or = [{ subject: 'NONE' }];
      }
      const handle = getCollectionHandle<CourseDigital>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector);
        }),
        catchError(e => {
          logger.error(e);
          return of([] as CourseDigital[]);
        }),
      );
    };
  }

  @Subscription(() => [CourseDigital])
  public static subscribeDigitalCourseBySearch(@Arg('filter', () => CourseDigitalFilter) filter: CourseDigitalFilter) {
    return (gqlFields: BaseQLSelectionSet<CourseDigital>) => {
      const handle = getCollectionHandle<CourseDigital>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(
            publishFilter ? { $and: [publishFilter, filter.getMongoFilter()] } : filter.getMongoFilter(),
          );
        }),
        catchError(e => {
          logger.error(e);
          return of([] as CourseDigital[]);
        }),
      );
    };
  }
}
